/**
 * FileSystemProxy - Centralized permissions control and automatic journaling
 *
 * SINGLE SOURCE OF TRUTH for filesystem permissions and file operation tracking.
 *
 * ARCHITECTURE:
 *
 * PRIMARY ENFORCEMENT (TypeScript interceptors):
 *   - permissionsEnabled flag: toggles permission checking
 *     * false = System file loading (bypass read-only checks)
 *     * true = User code execution (enforce read-only protection)
 *   - trustedOperationDepth counter: bypass for specific system operations
 *     * > 0 = Trusted operation (can write to read-only dirs)
 *     * = 0 = User operation (read-only enforcement applies)
 *   - interceptWriteFile/interceptOpen: primary gate, blocks writes to read-only dirs
 *
 * SECONDARY ENFORCEMENT (Python hooks - defense-in-depth):
 *   - _hooked_open(): blocks open() calls that bypass TypeScript
 *   - _hooked_os_open(): blocks low-level os.open() calls
 *   - Safety net if TypeScript layer is bypassed
 *
 * READ-ONLY DIRECTORIES (populated by system, protected from user):
 *   - ./default/ - workflow default files (from workflow.defaults)
 *   - ./project/ - project modules (from Module List with versionId)
 *
 * WRITABLE DIRECTORY (user output):
 *   - ./files/ - user-created files (persist to Notebook Files)
 *
 * USAGE:
 *   // System file loading (bypass read-only)
 *   fsProxy.disablePermissions();
 *   await loadProjectFiles();
 *   fsProxy.enablePermissions();
 *
 *   // OR wrap specific operations
 *   fsProxy.executeTrustedOperation(() => {
 *     python.FS.writeFile('./project/module.py', content);
 *   });
 *
 * IMPORTANT: Do NOT use chmod() - Pyodide MEMFS chmod() sets inode.mode which
 * blocks writes at low level, even when permissionsEnabled=false. Use TypeScript
 * interceptors for permission control instead.
 *
 * @see PROT-810 for read-only enforcement requirements
 */

import { PyodideFileJournal } from '../journal/python-journal.helper';
import {
  FileSystemProxyConfig,
  FileOperationType,
  WriteTrackingEntry,
  JournalDecision
} from '../interfaces/python-filesystem-proxy.interface';
import { PYTHON_DIRECTORIES } from '../interfaces/python.interface';
import { PythonUtils } from '../../../helpers/python-utils.helper';
import { GeneralHelpers } from '../../../helpers/general.helper';
import { FILE_EXTENSIONS } from '../../../constants/general.constants';

export class FileSystemProxy {
  private readonly originalFS: any;
  private readonly journal: PyodideFileJournal;
  private readonly config: FileSystemProxyConfig;

  /** Track open file descriptors for write operations */
  private readonly writeTracking: Map<number, WriteTrackingEntry> = new Map();

  /** Counter for trusted system operations (loading files to read-only dirs) */
  private trustedOperationDepth = 0;

  /** Permission enforcement toggle - OFF during system file loading, ON during user code execution */
  private permissionsEnabled = false;  // Start disabled for init

  /** Emscripten's EACCES, which Python raises as PermissionError (set by installFsGuards) */
  private eaccesCode = 2;

  constructor(
    originalFS: any,
    journal: PyodideFileJournal,
    config: FileSystemProxyConfig
  ) {
    this.originalFS = originalFS;
    this.journal = journal;

    // Set safe defaults - "Delete by Default" philosophy
    this.config = {
      // By default, files are temporary (persistent=false)
      defaultPersistent: config.defaultPersistent ?? false,

      // No default rules - programmer configures explicitly if needed
      persistenceRules: config.persistenceRules,  // undefined by default

      workingDir: config.workingDir || PYTHON_DIRECTORIES.WORKING_DIR,
      excludePaths: config.excludePaths || [],  // Default: empty = journal all paths
      enableLogging: config.enableLogging ?? false,
      includeExtensions: config.includeExtensions,  // Default: undefined = journal all extensions
      minFileSize: config.minFileSize ?? 0  // Default: 0 = journal all sizes
    };
  }

  /**
   * Execute an operation with system privileges (trusted operation).
   *
   * USAGE: System file loading operations that need to write to read-only directories.
   * Uses counter pattern to support nested trusted operations safely.
   *
   * PROT-810: Allows system to load files into default/ and project/ during initialization,
   * while still blocking user Python code from writing to these directories.
   *
   * @param operation - Function to execute with system privileges
   * @returns Result of the operation
   *
   * EXAMPLE:
   *   fsProxy.executeTrustedOperation(() => {
   *     python.FS.writeFile('/home/pyodide/project/module.py', content);
   *   });
   */
  public executeTrustedOperation<T>(operation: () => T): T {
    this.trustedOperationDepth++;
    try {
      return operation();
    } finally {
      // Always decrement, even if operation throws
      this.trustedOperationDepth--;
    }
  }

  /**
   * Check if current operation is trusted (system operation vs user code).
   *
   * PROT-810: System operations have trustedOperationDepth > 0 and bypass read-only checks.
   * User operations have trustedOperationDepth = 0 and are subject to read-only enforcement.
   *
   * @returns true if operation is trusted (system), false if user code
   */
  private isTrustedOperation(): boolean {
    return this.trustedOperationDepth > 0;
  }

  /**
   * Disable permission enforcement (for system file loading)
   */
  public disablePermissions(): void {
    this.permissionsEnabled = false;
  }

  /**
   * Enable permission enforcement (for user code execution)
   */
  public enablePermissions(): void {
    this.permissionsEnabled = true;
  }

  /**
   * Check if a path is within a read-only directory.
   * Returns true for default/ and project/ subdirectories.
   *
   * PROT-810: Enforce read-only directories since Pyodide MEMFS doesn't support chmod.
   *
   * @param path - Normalized absolute path to check
   * @returns true if path is read-only, false otherwise
   */
  private isReadOnlyPath(path: string): boolean {
    // Define read-only directories (default/ and project/)
    // These directories are populated by system during initialization,
    // but user code should not modify them during execution
    const readOnlyDirs = [
      `${this.config.workingDir}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}`,  // /home/pyodide/default
      `${this.config.workingDir}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}`   // /home/pyodide/project
    ];

    // Check if path is inside or is a read-only directory
    for (const readOnlyDir of readOnlyDirs) {
      // Exact match (trying to write to directory itself)
      if (path === readOnlyDir) {
        return true;
      }

      // Path inside directory (trying to write file in read-only dir)
      if (path.startsWith(readOnlyDir + '/')) {
        return true;
      }
    }

    return false;
  }

  /**
   * The read-only folder a user write would land in, or undefined when it's allowed.
   *
   * Checks the path as given and with symlinks resolved: the root links
   * (/project, /default, /workflow) and any link a step creates would otherwise
   * reach project/ or default/ under another name (EHQ-545).
   *
   * normalizePath collapses `..` as text, but after a link `..` is the link
   * target's parent, so the raw path is resolved as well.
   *
   * @param normalizedPath - Path from PythonUtils.normalizePath
   * @param rawPath - The path as the caller gave it
   * @returns The path to report in the PermissionError, or undefined
   */
  private readOnlyTarget(normalizedPath: string, rawPath: string): string | undefined {
    if (!this.permissionsEnabled || this.isTrustedOperation()) {
      return undefined;
    }
    const absoluteRaw = rawPath.startsWith('/') ? rawPath : `${this.config.workingDir}/${rawPath}`;
    for (const candidate of [normalizedPath, this.resolveSymlinks(normalizedPath), this.resolveSymlinks(absoluteRaw)]) {
      if (this.isReadOnlyPath(candidate)) {
        return candidate;
      }
    }
    return undefined;
  }

  /**
   * Resolve every symlink in an absolute path, like realpath, except that
   * missing components are kept as they are (the write may be creating them).
   * A dangling link is followed to its target, since a write through it
   * creates the target.
   */
  private resolveSymlinks(path: string): string {
    const fs = this.originalFS;
    if (typeof fs.lstat !== 'function' || typeof fs.readlink !== 'function' || typeof fs.isLink !== 'function') {
      return path;
    }

    const pending = path.split('/').filter(Boolean);
    const resolved: string[] = [];
    let missing = false;
    let linksFollowed = 0;

    while (pending.length > 0) {
      const part = pending.shift()!;
      if (part === '.') continue;
      if (part === '..') {
        resolved.pop();
        continue;
      }

      const current = `/${[...resolved, part].join('/')}`;
      let target: string | undefined;
      if (!missing) {
        try {
          if (fs.isLink(fs.lstat(current).mode)) {
            target = fs.readlink(current);
          }
        } catch {
          missing = true;  // Nothing below a missing component can be a link
        }
      }

      if (target === undefined) {
        resolved.push(part);
        continue;
      }

      // Emscripten's own limit (ELOOP); a loop can't be written through anyway.
      if (++linksFollowed > 40) {
        return current;
      }
      if (target.startsWith('/')) {
        resolved.length = 0;
      }
      pending.unshift(...target.split('/').filter(Boolean));
    }

    return `/${resolved.join('/')}`;
  }

  /**
   * Create a Python-compatible PermissionError with helpful message.
   *
   * PROT-810: Provide user-friendly error messages when write to read-only dir is attempted.
   *
   * @param path - The path that was denied
   * @param operation - The operation that was attempted (e.g., 'write', 'open for writing')
   * @returns Error object formatted like Python PermissionError
   */
  private createPermissionError(path: string, operation: string = 'write'): Error {
    // Extract directory name for user-friendly message
    const dirMatch = path.match(/\/(default|project)\//);
    const dirName = dirMatch ? dirMatch[1] : 'read-only';

    const error = new Error(
      `PermissionError: [Errno 13] Permission denied: Cannot ${operation} to '${path}'\n\n` +
      `The ${dirName}/ directory is read-only.\n` +
      `Please use files/ directory for user-created or modified files.\n\n` +
      `Example:\n` +
      `  ✗ Wrong: open('${dirName}/output.csv', 'w')\n` +
      `  ✓ Right: open('files/output.csv', 'w')`
    );
    error.name = 'PermissionError';
    return error;
  }

  /**
   * Guard the read-only folders on Pyodide's FS object itself (EHQ-545).
   *
   * Python's file calls (open, io, pathlib, posix, os.remove, os.rename,
   * shutil.rmtree's dir_fd calls, ...) reach these methods directly, with
   * absolute paths, and never pass through wrap()'s Proxy or the Python-level
   * hooks. Guarding the methods covers every route. A refused call throws
   * Emscripten's EACCES, which Python raises as PermissionError.
   *
   * A call that acts on a directory entry (unlink, rmdir, rename, mkdir, mknod,
   * symlink, lchmod) resolves links in the parent only, so a link that points
   * into project/ can still be removed. A call that acts on what the path
   * points to (a write open, truncate, chmod) resolves the whole path.
   * Trusted operations and a disabled proxy pass through, as elsewhere.
   *
   * @param errnoCodes - Pyodide's ERRNO_CODES, for EACCES
   */
  public installFsGuards(errnoCodes?: { EACCES?: number }): void {
    const fs = this.originalFS;
    if (fs.__readOnlyGuards) {
      return;
    }
    this.eaccesCode = errnoCodes?.EACCES ?? this.eaccesCode;

    const guard = (method: string, check: (args: any[]) => void) => {
      const original = fs[method];
      if (typeof original !== 'function') {
        return;
      }
      fs[method] = function (...args: any[]) {
        check(args);
        return original.apply(fs, args);
      };
    };

    guard('open', ([path, flags]) => {
      if (this.isWriteFlags(flags)) this.checkFsPath(path, 'target');
    });
    guard('truncate', ([path]) => this.checkFsPath(path, 'target'));
    guard('chmod', ([path]) => this.checkFsPath(path, 'target'));
    guard('lchmod', ([path]) => this.checkFsPath(path, 'entry'));
    guard('mknod', ([path]) => this.checkFsPath(path, 'entry'));
    guard('mkdir', ([path]) => this.checkFsPath(path, 'entry'));
    guard('symlink', ([, linkPath]) => this.checkFsPath(linkPath, 'entry'));
    guard('unlink', ([path]) => this.checkFsPath(path, 'entry', true));
    guard('rmdir', ([path]) => this.checkFsPath(path, 'entry', true));
    guard('rename', ([oldPath, newPath]) => {
      this.checkFsPath(oldPath, 'entry', true);
      this.checkFsPath(newPath, 'entry', true);
    });

    fs.__readOnlyGuards = true;
  }

  /** True when open flags (number or mode string) can create or change a file. */
  private isWriteFlags(flags: unknown): boolean {
    if (typeof flags === 'string') {
      return /[wax+]/.test(flags);
    }
    // O_WRONLY | O_RDWR | O_CREAT | O_TRUNC
    return typeof flags === 'number' && (flags & (0x3 | 0x40 | 0x200)) !== 0;
  }

  /**
   * Throw EACCES when a user call would change a read-only folder.
   *
   * @param path - Path as the FS method got it, or an FS node (fchmod, ftruncate)
   * @param mode - 'target' resolves the whole path; 'entry' resolves only its parent
   * @param orAncestor - also refuse a path that contains a read-only folder
   *   (renaming or removing /home/pyodide would take project/ with it)
   */
  private checkFsPath(path: unknown, mode: 'target' | 'entry', orAncestor = false): void {
    if (!this.permissionsEnabled || this.isTrustedOperation()) {
      return;
    }

    let candidates: string[];
    try {
      const raw = typeof path === 'string' ? path : this.originalFS.getPath(path);
      const absolute = raw.startsWith('/') ? raw : `${this.fsCwd()}/${raw}`;
      const resolved = mode === 'target' ? this.resolveSymlinks(absolute) : this.resolveEntry(absolute);
      candidates = [PythonUtils.normalizePath(absolute), resolved];
    } catch {
      // Can't tell where it lands: refuse rather than let it through.
      throw this.accessDenied();
    }

    for (const candidate of candidates) {
      if (this.isReadOnlyPath(candidate) || (orAncestor && this.containsReadOnlyDir(candidate))) {
        throw this.accessDenied();
      }
    }
  }

  /** The entry a path names: links in its parent resolved, the last component kept. */
  private resolveEntry(absolutePath: string): string {
    const cut = absolutePath.lastIndexOf('/');
    const name = absolutePath.slice(cut + 1);
    if (name === '' || name === '.' || name === '..') {
      return this.resolveSymlinks(absolutePath);
    }
    const parent = this.resolveSymlinks(absolutePath.slice(0, cut) || '/');
    return parent === '/' ? `/${name}` : `${parent}/${name}`;
  }

  /** True when a read-only folder sits somewhere below path. */
  private containsReadOnlyDir(path: string): boolean {
    const prefix = path === '/' ? '/' : `${path}/`;
    return [PYTHON_DIRECTORIES.DEFAULT_SUBDIR, PYTHON_DIRECTORIES.PROJECT_SUBDIR]
      .some((dir) => `${this.config.workingDir}/${dir}`.startsWith(prefix));
  }

  private fsCwd(): string {
    return typeof this.originalFS.cwd === 'function' ? this.originalFS.cwd() : this.config.workingDir!;
  }

  /** Emscripten's EACCES error, so a Python caller gets PermissionError. */
  private accessDenied(): Error {
    const ErrnoError = this.originalFS.ErrnoError;
    if (typeof ErrnoError === 'function') {
      return new ErrnoError(this.eaccesCode);
    }
    const error = new Error('PermissionError: [Errno 13] Permission denied: the default/ and project/ folders are read-only');
    error.name = 'PermissionError';
    return error;
  }

  /**
   * Wrap the filesystem with a Proxy that intercepts operations
   */
  wrap(fs: any): any {
    const self = this;

    return new Proxy(fs, {
      get(target, prop: string) {
        // Intercept write operations
        if (prop === 'writeFile') {
          return function (path: string, data: any, opts?: any) {
            return self.interceptWriteFile(path, data, opts);
          };
        }

        // Intercept open/close for tracking
        if (prop === 'open') {
          return function (path: string, flags: any, mode?: any) {
            return self.interceptOpen(path, flags, mode);
          };
        }

        if (prop === 'close') {
          return function (stream: any) {
            return self.interceptClose(stream);
          };
        }

        // Intercept write for tracking bytes
        if (prop === 'write') {
          return function (stream: any, buffer: any, offset?: any, length?: any, position?: any) {
            return self.interceptWrite(stream, buffer, offset, length, position);
          };
        }

        // Intercept rename
        if (prop === 'rename') {
          return function (oldPath: string, newPath: string) {
            return self.interceptRename(oldPath, newPath);
          };
        }

        // Intercept unlink (delete)
        if (prop === 'unlink') {
          return function (path: string) {
            return self.interceptUnlink(path);
          };
        }

        // Intercept directory creation
        if (prop === 'mkdir' || prop === 'mkdirTree') {
          return function (path: string, mode?: any) {
            return self.interceptMkdir(path, mode, prop === 'mkdirTree');
          };
        }

        // Return original method for everything else
        // CRITICAL FIX: Bind function to target to preserve correct 'this' context
        // Pyodide FS methods (especially writeFile) require proper WASM context
        const value = target[prop];
        if (typeof value === 'function') {
          return function (...args: any[]) {
            return value.apply(target, args);
          };
        }
        return value;
      }
    });
  }

  /**
   * Intercept writeFile - main entry point for file writes
   */
  private interceptWriteFile(path: string, data: any, opts?: any): void {
    // Normalize path
    const normalizedPath = PythonUtils.normalizePath(path, this.config.workingDir);

    // PROT-810: Check read-only ONLY for user operations (not system)
    // System operations (trustedOperationDepth > 0) bypass read-only check
    // This allows system to load files into default/project during initialization

    // Check read-only ONLY if permissions enabled
    const readOnly = this.readOnlyTarget(normalizedPath, path);
    if (readOnly) {
      throw this.createPermissionError(readOnly, 'write');
    }

    // Check if should journal
    const decision = this.shouldJournal(normalizedPath, FileOperationType.WRITE);
    if (!decision.shouldJournal) {
      return this.originalFS.writeFile(normalizedPath, data, opts);
    }

    // Extract metadata
    const size = this.calculateSize(data);
    const fileType = this.extractFileType(normalizedPath);

    // Determine if file should be persistent using smart rules
    const persistent = this.determinePersistence(normalizedPath, data);

    // Journal the operation
    try {
      this.journal.logFileCreation(normalizedPath, 'generated', {
        origin: 'generated',
        fileType,
        timestamp: Date.now(),
        persistent: persistent,  // Smart determination based on rules
        purpose: 'output',
        size,
        creator: 'python-runtime'
      });
    } catch (error) {
      console.debug('Python FS proxy: journal write failed', { normalizedPath, error });
    }

    // Call original method with normalized path
    return this.originalFS.writeFile(normalizedPath, data, opts);
  }

  /**
   * Intercept open - track file descriptors for write operations
   */
  private interceptOpen(path: string, flags: any, mode?: any): any {
    // Check if this is a write operation
    // Common flags: O_WRONLY (1), O_RDWR (2), O_CREAT (64), O_TRUNC (512)
    const isWrite = typeof flags === 'number' && (flags & 0x3) !== 0; // O_WRONLY or O_RDWR

    // Normalize path once at the beginning
    const normalizedPath = PythonUtils.normalizePath(path, this.config.workingDir);

    // PROT-810: Check read-only directories BEFORE opening for write
    // BUT allow system operations (file loading) to proceed
    if (isWrite) {
      // Skip read-only check for trusted system operations
      const readOnly = this.readOnlyTarget(normalizedPath, path);
      if (readOnly) {
        throw this.createPermissionError(readOnly, 'open for writing');
      }
    }

    // Use normalized path in originalFS call
    const result = this.originalFS.open(normalizedPath, flags, mode);

    if (isWrite) {
      const decision = this.shouldJournal(normalizedPath, FileOperationType.OPEN);

      if (decision.shouldJournal && result && typeof result === 'object' && result.fd) {
        // Track this file descriptor
        this.writeTracking.set(result.fd, {
          path: normalizedPath,
          bytesWritten: 0,
          openTime: Date.now(),
          fd: result.fd
        });
      }
    }

    return result;
  }

  /**
   * Intercept write - track bytes written for open files
   */
  private interceptWrite(stream: any, buffer: any, offset?: any, length?: any, position?: any): any {
    // Call original write first
    const result = this.originalFS.write(stream, buffer, offset, length, position);

    // Track bytes written if this is a tracked file descriptor
    const fd = stream?.fd;
    if (fd !== undefined && this.writeTracking.has(fd)) {
      // Calculate bytes written
      const bytesWritten = buffer?.length || buffer?.byteLength || (length !== undefined ? length : 0);

      // Update tracking
      const tracking = this.writeTracking.get(fd)!;
      tracking.bytesWritten += bytesWritten;
    }

    return result;
  }

  /**
   * Intercept close - journal file if it was tracked
   */
  private interceptClose(stream: any): any {
    const fd = stream?.fd;

    if (fd !== undefined && this.writeTracking.has(fd)) {
      const tracking = this.writeTracking.get(fd)!;

      // Journal the file with accumulated size
      try {
        const fileType = this.extractFileType(tracking.path);

        // PROT-810 FIX: Use determinePersistence() instead of hardcoded true
        // This ensures files follow the same persistence rules regardless of creation method
        const persistent = this.determinePersistence(tracking.path, {
          length: tracking.bytesWritten
        });

        this.journal.logFileCreation(tracking.path, 'generated', {
          origin: 'generated',
          fileType,
          timestamp: Date.now(),
          persistent: persistent,  // Use smart determination (was: hardcoded true)
          purpose: 'output',
          size: tracking.bytesWritten,
          creator: 'python-runtime'
        });
      } catch (error) {
        console.debug('Python FS proxy: journal close failed', { path: tracking.path, error });
      }

      // Remove from tracking
      this.writeTracking.delete(fd);
    }

    return this.originalFS.close(stream);
  }

  /**
   * Intercept rename - update journal
   */
  private interceptRename(oldPath: string, newPath: string): any {
    const normalizedOld = PythonUtils.normalizePath(oldPath, this.config.workingDir);
    const normalizedNew = PythonUtils.normalizePath(newPath, this.config.workingDir);

    return this.originalFS.rename(normalizedOld, normalizedNew);
  }

  /**
   * Intercept unlink - remove from journal
   */
  private interceptUnlink(path: string): any {
    const normalizedPath = PythonUtils.normalizePath(path, this.config.workingDir);
    return this.originalFS.unlink(normalizedPath);
  }

  /**
   * Intercept mkdir - log directory creation
   * Updated: PROT-777 fix - now journals user-created directories
   */
  private interceptMkdir(path: string, mode?: any, isTree: boolean = false): any {
    const methodName = isTree ? 'mkdirTree' : 'mkdir';
    const normalizedPath = PythonUtils.normalizePath(path, this.config.workingDir);

    // Same read-only rule as writeFile and open (EHQ-542): system loading runs
    // as a trusted operation and may create folders under default/ and project/.
    const readOnly = this.readOnlyTarget(normalizedPath, path);
    if (readOnly) {
      throw this.createPermissionError(readOnly, 'create a folder');
    }

    // Check if should journal (skip system dirs like /lib, /tmp)
    const decision = this.shouldJournal(normalizedPath, FileOperationType.MKDIR);

    if (decision.shouldJournal) {
      // Reuse existing journal.logDirectoryCreation() method
      this.journal.logDirectoryCreation(normalizedPath);
    }

    return this.originalFS[methodName](normalizedPath, mode);
  }

  /**
   * Check if a file operation should be journaled
   *
   * Default behavior: Journal EVERYTHING unless explicitly filtered
   * Filters only apply if configured
   */
  private shouldJournal(path: string, _operation: FileOperationType): JournalDecision {
    // 1. Check exclude paths (only if configured)
    if (this.config.excludePaths && this.config.excludePaths.length > 0) {
      for (const excludePath of this.config.excludePaths) {
        if (path.startsWith(excludePath)) {
          return {
            shouldJournal: false,
            reason: `Path excluded: ${excludePath}`
          };
        }
      }
    }

    // 2. Check file extension filter (only if configured)
    if (this.config.includeExtensions && this.config.includeExtensions.length > 0) {
      const fileType = this.extractFileType(path);
      if (!this.config.includeExtensions.includes(`.${fileType}`)) {
        return {
          shouldJournal: false,
          reason: `Extension not in filter: .${fileType}`
        };
      }
    }

    // 3. Always skip system files (regardless of config)
    if (path.startsWith('/lib/') ||
      path.includes('/site-packages/') ||
      path.startsWith('/usr/') ||
      path.startsWith('/bin/') ||
      path.startsWith('/sbin/') ||
      path.startsWith('/dev/') ||
      path.startsWith('/proc/')) {
      return {
        shouldJournal: false,
        reason: `System path: ${path}`
      };
    }

    // Default: Journal user files ✅
    return { shouldJournal: true };
  }

  /**
   * Determine if a file should be persistent based on rules
   *
   * Default: false (DELETE BY DEFAULT)
   * Checks persistence rules to decide if file should survive cleanup
   */
  private determinePersistence(path: string, data: any): boolean {
    const rules = this.config.persistenceRules;

    // Check prefix rules (e.g., 'cache_', 'saved_')
    if (rules?.prefixes && rules.prefixes.length > 0) {
      for (const prefix of rules.prefixes) {
        if (path.includes(prefix)) {
          return true;  // File has persistent prefix
        }
      }
    }

    // Check extension rules (e.g., '.cache', '.persistent')
    if (rules?.extensions && rules.extensions.length > 0) {
      const fileType = this.extractFileType(path);
      if (rules.extensions.includes(`.${fileType}`)) {
        return true;  // File has persistent extension
      }
    }

    // Check path rules (e.g., '/persistent/')
    if (rules?.paths && rules.paths.length > 0) {
      for (const rulePath of rules.paths) {
        if (path.startsWith(rulePath)) {
          return true;  // File in persistent directory
        }
      }
    }

    // Check size limit
    if (rules?.maxSize) {
      const size = this.calculateSize(data);
      if (size > rules.maxSize) {
        return false;  // File too large, don't persist
      }
    }

    // Temporary directory - never persist
    // Safe: Pyodide TEMP_DIR is isolated virtual filesystem in browser, not OS /tmp
    if (path.startsWith(PYTHON_DIRECTORIES.TEMP_DIR)) {
      return false;
    }

    // Wheel files - don't persist after installation
    const extension = GeneralHelpers.fileExtensionFromString(path);
    if (extension === FILE_EXTENSIONS.whl) {
      return false;
    }

    // Use configured default or false
    return this.config.defaultPersistent ?? false;
  }

  /**
   * Extract file type from path
   */
  private extractFileType(path: string): string {
    const parts = path.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Calculate size of data
   */
  private calculateSize(data: any): number {
    if (data instanceof ArrayBuffer) {
      return data.byteLength;
    } else if (data instanceof Uint8Array) {
      return data.byteLength;
    } else if (typeof data === 'string') {
      return data.length;
    } else if (data && typeof data === 'object' && 'length' in data) {
      return (data as any).length;
    }
    return 0;
  }

  /**
   * Install Python-level hooks to intercept file operations
   * that bypass the JavaScript FS layer (pandas, pyarrow, numpy, etc.)
   */
  public installPythonHooks(python: any): void {
    // Register JS module for journal callback (proper Pyodide way)
    python.registerJsModule('fs_journal', {
      logFile: (path: string, size: number) => {
        try {
          const normalizedPath = PythonUtils.normalizePath(path, this.config.workingDir);
          const fileType = this.extractFileType(normalizedPath);
          const persistent = this.determinePersistence(normalizedPath, { length: size });

          this.journal.logFileCreation(normalizedPath, 'generated', {
            origin: 'generated',
            fileType,
            timestamp: Date.now(),
            persistent: persistent,
            purpose: 'output',
            size,
            creator: 'python-builtins'
          });
        } catch (error) {
          console.warn('Python FS proxy: hook file journal failed', { path, error });
        }
      },

      // PROT-777 fix: Directory creation callback
      logDir: (path: string) => {
        try {
          const normalizedPath = PythonUtils.normalizePath(path, this.config.workingDir);

          // Reuse existing journal.logDirectoryCreation() method
          this.journal.logDirectoryCreation(normalizedPath);
        } catch (error) {
          console.warn('Python FS proxy: hook directory journal failed', { path, error });
        }
      }
    });

    // Install Python monkey-patches
    const pythonHooksCode = `
import builtins
import os
import sys

# Check if hooks already installed (idempotency)
if hasattr(builtins.open, '_is_fs_hooked'):
    print("ℹ️  Python file operation hooks already installed, skipping")
else:
    # Storage for tracking open file descriptors
    _open_file_tracking = {}

    # Backup original open
    _original_open = builtins.open

    _READ_ONLY_DIRS = [
        '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}/',
        '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}/'
    ]

    def _read_only_target(file):
        """(path, read-only dir) for a write to file, or (path, None) when it's allowed.

        Checks the path as given and with symlinks resolved, so the root links
        (/project, /default, /workflow) can't reach a read-only dir under another
        name (EHQ-545). A file descriptor isn't a path and is left to the open it came from.
        """
        if isinstance(file, int):
            return str(file), None
        try:
            file_path = os.fsdecode(file)
        except TypeError:
            file_path = str(file)
        # No try: if a path can't be resolved (a null byte, say), the error
        # propagates and the open fails. Skipping the check would let it through.
        for candidate in (os.path.abspath(file_path), os.path.realpath(file_path)):
            for read_only_dir in _READ_ONLY_DIRS:
                if candidate.startswith(read_only_dir) or candidate == read_only_dir.rstrip('/'):
                    return candidate, read_only_dir
        return file_path, None

    def _hooked_open(file, mode='r', buffering=-1, encoding=None, errors=None, newline=None, closefd=True, opener=None):
        """Hooked version of builtins.open that tracks file writes and enforces read-only directories (PROT-810)"""

        # PROT-810: Check read-only directories for write operations
        # Secondary defense layer - blocks write attempts that bypass TypeScript interceptors
        # '+' too: 'r+' modifies an existing file
        if any(m in str(mode) for m in ['w', 'a', 'x', '+']):
            file_path, read_only_dir = _read_only_target(file)
            if read_only_dir:
                dir_name = read_only_dir.rstrip('/').split('/')[-1]
                raise PermissionError(
                    f"[Errno 13] Permission denied: Cannot write to '{file_path}'\\n\\n"
                    f"The {dir_name}/ directory is read-only.\\n"
                    f"Please use files/ directory for user-created or modified files.\\n\\n"
                    f"Example:\\n"
                    f"  ✗ Wrong: open('{dir_name}/output.csv', 'w')\\n"
                    f"  ✓ Right: open('files/output.csv', 'w')"
                )

        # Call original open
        fp = _original_open(file, mode, buffering, encoding, errors, newline, closefd, opener)

        # Track write operations
        if any(m in str(mode) for m in ['w', 'a', 'x']):
            try:
                fd = fp.fileno()
                file_path = file if isinstance(file, str) else str(file)
                _open_file_tracking[fd] = {
                    'path': file_path,
                    'bytes_written': 0,
                    'mode': mode
                }
            except Exception as e:
                pass  # fileno() may not be available for all file objects

        # Wrap file object to track close
        original_close = fp.close
        def _tracked_close():
            try:
                fd = fp.fileno()
                if fd in _open_file_tracking:
                    info = _open_file_tracking.pop(fd)
                    # Get file size
                    try:
                        size = os.path.getsize(info['path'])
                        # Only journal user files (not system files)
                        is_user_file = not info['path'].startswith('/lib/') and not info['path'].startswith('${PYTHON_DIRECTORIES.TEMP_DIR}')

                        if is_user_file:
                            # Call JS callback only for user files
                            import fs_journal
                            fs_journal.logFile(info['path'], size)
                    except Exception as e:
                        pass  # Silently fail for non-critical journaling errors
                else:
                    # Don't log read operations for system files
                    pass
            except Exception as e:
                print(f"⚠️  Python hook - Error in close tracking: {e}")
            original_close()

        fp.close = _tracked_close

        return fp

    # Apply the patch. io.open is a separate binding (pathlib and io callers use it).
    import io
    builtins.open = _hooked_open
    io.open = _hooked_open

    # Mark as installed to prevent double-hooking
    _hooked_open._is_fs_hooked = True

    # ========================================================================
    # Patch os module for low-level file operations (used by pyarrow, numpy)
    # ========================================================================

    # Storage for tracking os.open file descriptors
    _os_fd_tracking = {}

    # Patch os.open
    _original_os_open = os.open

    def _hooked_os_open(path, flags, mode=0o777, *args, **kwargs):
        """Hooked os.open to track low-level file operations and enforce read-only directories (PROT-810)"""

        # PROT-810: Check read-only directories for write operations
        # Secondary defense layer - blocks low-level write attempts that bypass TypeScript
        # A dir_fd call (shutil uses them) names a path relative to that fd, which
        # can't be checked here; the FS-level guard sees the full path (EHQ-545).
        if flags & (os.O_WRONLY | os.O_RDWR | os.O_CREAT) and kwargs.get('dir_fd') is None:
            _, read_only_dir = _read_only_target(path)
            if read_only_dir:
                dir_name = read_only_dir.rstrip('/').split('/')[-1]
                # Raise OSError (os.open returns OSError, not PermissionError)
                import errno
                raise OSError(
                    errno.EACCES,
                    f"Permission denied: Cannot write to read-only directory '{dir_name}/'"
                )

        fd = _original_os_open(path, flags, mode, *args, **kwargs)

        # Track write operations (O_WRONLY=1, O_RDWR=2, O_CREAT=64)
        if flags & (os.O_WRONLY | os.O_RDWR | os.O_CREAT):
            _os_fd_tracking[fd] = {
                'path': path if isinstance(path, str) else str(path),
                'bytes_written': 0
            }

        return fd

    # Patch os.write
    _original_os_write = os.write

    def _hooked_os_write(fd, data):
        """Hooked os.write to track bytes written"""
        result = _original_os_write(fd, data)
        if fd in _os_fd_tracking:
            _os_fd_tracking[fd]['bytes_written'] += len(data)
        return result

    # Patch os.close
    _original_os_close = os.close

    def _hooked_os_close(fd):
        """Hooked os.close to journal file on close"""
        if fd in _os_fd_tracking:
            info = _os_fd_tracking.pop(fd)
            try:
                # Get actual file size
                size = os.path.getsize(info['path'])
                # Only journal user files (not system files)
                is_user_file = not info['path'].startswith('/lib/') and not info['path'].startswith('${PYTHON_DIRECTORIES.TEMP_DIR}')

                if is_user_file:
                    # Call JS callback only for user files
                    import fs_journal
                    fs_journal.logFile(info['path'], size)
            except Exception as e:
                pass  # Silently fail for non-critical journaling errors
        return _original_os_close(fd)

    # Apply os patches
    os.open = _hooked_os_open
    os.write = _hooked_os_write
    os.close = _hooked_os_close

    # ========================================================================
    # Patch os.makedirs for user-created subdirectories (PROT-777 fix)
    # ========================================================================

    _original_makedirs = os.makedirs

    def _hooked_makedirs(name, mode=0o777, exist_ok=False):
        """Hooked os.makedirs to journal directory creation"""
        # Call original first
        result = _original_makedirs(name, mode, exist_ok)

        # Journal only user directories (skip system dirs)
        is_user_dir = not name.startswith('/lib/') and not name.startswith('${PYTHON_DIRECTORIES.TEMP_DIR}')

        if is_user_dir:
            try:
                import fs_journal
                fs_journal.logDir(name)  # Call JS callback
            except Exception:
                pass  # Non-critical - don't break user code

        return result

    os.makedirs = _hooked_makedirs

    # Hook os.rmdir to protect structural directories
    _original_rmdir = os.rmdir

    def _hooked_rmdir(path, *args, **kwargs):
        """Hooked os.rmdir to protect structural directories"""
        protected_dirs = [
            '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}',
            '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}',
            '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}'
        ]

        # With dir_fd (shutil.rmtree) the path is relative to that fd; the
        # FS-level guard still covers default/ and project/ there.
        if kwargs.get('dir_fd') is None and os.path.abspath(path) in protected_dirs:
            raise PermissionError(f"Cannot delete structural directory: {path}")

        return _original_rmdir(path, *args, **kwargs)

    os.rmdir = _hooked_rmdir

    # Hook shutil.rmtree to protect structural directories
    try:
        import shutil
        _original_rmtree = shutil.rmtree

        def _hooked_rmtree(path, *args, **kwargs):
            """Hooked shutil.rmtree to protect structural directories"""
            protected_dirs = [
                '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}',
                '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}',
                '${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}'
            ]

            abs_path = os.path.abspath(path)
            if abs_path in protected_dirs:
                raise PermissionError(f"Cannot delete structural directory: {path}")

            return _original_rmtree(path, *args, **kwargs)

        shutil.rmtree = _hooked_rmtree
    except ImportError:
        pass  # shutil not available

    print("✅ Python file operation hooks installed (builtins.open + os module + os.makedirs + os.rmdir + shutil.rmtree)")
`;

    try {
      python.runPython(pythonHooksCode);
    } catch (error) {
      console.warn('Python FS proxy: failed to install Python hooks', error);
    }
  }
}
